
import React, { useState, useEffect } from 'react';
import { AccountType, SystemSettings } from '../types';

interface RobotSetupProps {
  onStart: (amount: number, accountType: AccountType) => void;
  accountType: AccountType;
  setActiveAccountType: (type: AccountType) => void;
  balance: number;
  systemSettings: SystemSettings | null;
}

const RobotSetup: React.FC<RobotSetupProps> = ({ onStart, accountType, setActiveAccountType, balance, systemSettings }) => {
  const [amount, setAmount] = useState<string>('100');
  const [estProfitMin, setEstProfitMin] = useState(0);
  const [estProfitMax, setEstProfitMax] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const amt = parseFloat(amount) || 0;
    const minYield = systemSettings?.dailyMinProfit || 8;
    const maxYield = systemSettings?.dailyMaxProfit || 24;
    
    // Calculate estimated profit based on percentages from admin settings
    setEstProfitMin(amt * (minYield / 100));
    setEstProfitMax(amt * (maxYield / 100));
  }, [amount, systemSettings]);

  const handleInitialClick = () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    if (amt > balance) {
      alert("Insufficient balance for this trade.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmStart = () => {
    setShowConfirmModal(false);
    onStart(parseFloat(amount), accountType);
  };

  return (
    <div className="max-w-3xl mx-auto animate-in fade-in duration-500 pb-16">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-lg md:text-2xl font-black uppercase tracking-tighter text-white mb-0.5">Robot_Setup</h1>
          <div className="flex items-center gap-1.5">
             <div className="w-1 h-1 bg-[#AEB877] rounded-full shadow-[0_0_10px_#AEB877] animate-pulse"></div>
             <p className="text-[7px] text-white/20 font-black uppercase tracking-[0.5em]">Status: Awaiting_Parameters</p>
          </div>
        </div>
        <button className="text-white/20 text-[7px] flex items-center gap-1.5 hover:text-[#AEB877] transition-all font-black uppercase tracking-[0.4em] group">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="group-hover:rotate-12 transition-transform"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
          Protocol_Guide
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-card p-4 relative overflow-hidden group">
            <label className="block text-[7px] font-black text-white/20 uppercase tracking-[0.5em] mb-3">Trade_Allocation (Basis)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AEB877] font-black text-lg">$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-black/40 border border-white/5 rounded-xl py-3 pl-8 pr-3 text-white font-black text-xl focus:outline-none focus:border-[#AEB877]/40 transition-all tracking-tighter tabular-nums"
                placeholder="0.00"
              />
            </div>
            <div className="mt-3 flex items-center justify-between">
               <p className="text-[7px] font-black text-white/10 uppercase tracking-[0.3em]">Available_Liquidity</p>
               <p className="text-xs font-black text-white tracking-tighter">${balance.toLocaleString()}</p>
            </div>
          </div>

          <div className="glass-card p-4 relative overflow-hidden">
            <label className="block text-[7px] font-black text-white/20 uppercase tracking-[0.5em] mb-3">Account_Protocol_Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setActiveAccountType(AccountType.DEMO)}
                className={`py-3 rounded-xl font-black text-[8px] tracking-[0.4em] transition-all uppercase border ${
                  accountType === AccountType.DEMO ? 'bg-white text-black border-white shadow-2xl' : 'bg-white/5 text-white/20 border-white/5 hover:text-white'
                }`}
              >
                Demo_Mode
              </button>
              <button
                onClick={() => setActiveAccountType(AccountType.LIVE)}
                className={`py-3 rounded-xl font-black text-[8px] tracking-[0.4em] transition-all uppercase border ${
                  accountType === AccountType.LIVE ? 'bg-[#FF4D4D] text-white border-[#FF4D4D] shadow-2xl' : 'bg-white/5 text-white/20 border-white/5 hover:text-white'
                }`}
              >
                Live_Mode
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass-card p-4 relative overflow-hidden h-full flex flex-col justify-center text-center">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#AEB877]/[0.05] to-transparent"></div>
            <p className="text-[7px] font-black text-white/20 uppercase tracking-[0.5em] mb-3 relative z-10">Estimated_24H_Yield</p>
            <p className="text-xl md:text-2xl font-black text-[#AEB877] tracking-tighter tabular-nums glow-text relative z-10">
              ${estProfitMin.toFixed(2)}
            </p>
            <div className="h-px w-6 bg-white/5 mx-auto my-2 relative z-10"></div>
            <p className="text-xl md:text-2xl font-black text-[#AEB877] tracking-tighter tabular-nums glow-text relative z-10">
              ${estProfitMax.toFixed(2)}
            </p>
            <p className="text-[7px] font-black text-white/10 uppercase tracking-[0.3em] mt-3 relative z-10">Projection_Matrix</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="glass-card p-4 flex items-center justify-between group">
          <span className="text-white/20 text-[7px] font-black uppercase tracking-[0.4em]">Crypto_Exchange</span>
          <div className="flex items-center gap-2">
            <span className="font-black tracking-tighter text-sm text-white">BYB<span className="text-[#AEB877]">I</span>T</span>
            <div className="w-3 h-3 bg-[#AEB877]/10 rounded-md flex items-center justify-center border border-[#AEB877]/20">
              <div className="w-1 h-1 bg-[#AEB877] shadow-[0_0_10px_#AEB877] rounded-full"></div>
            </div>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center justify-between group">
          <span className="text-white/20 text-[7px] font-black uppercase tracking-[0.4em]">Forex_Broker</span>
          <div className="flex items-center gap-2">
            <span className="font-black flex items-center gap-1.5 text-sm text-white">
              <span className="bg-[#FF4D4D] text-[7px] px-1 py-0.5 font-black text-white rounded-md">X</span>
              xtb
            </span>
            <div className="w-3 h-3 bg-[#AEB877]/10 rounded-md flex items-center justify-center border border-[#AEB877]/20">
              <div className="w-1 h-1 bg-[#AEB877] shadow-[0_0_10px_#AEB877] rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card overflow-hidden mb-6 shadow-2xl">
        <div className="p-4 border-b border-white/5 flex items-start gap-4 bg-white/[0.02]">
          <div className="w-10 h-10 bg-[#AEB877]/10 rounded-xl flex items-center justify-center text-[#AEB877] border border-[#AEB877]/20 shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div>
            <p className="font-black uppercase tracking-tighter text-lg text-white mb-0.5">Neural_Scalping_Protocol</p>
            <p className="text-[8px] text-white/20 font-black leading-relaxed uppercase tracking-widest">Algorithm executes sub-second entries targeting 0.4% - 1.2% per cycle. Operating efficiency: 98.4%.</p>
          </div>
        </div>
        <div className="grid grid-cols-2">
          <div className="p-4 border-r border-white/5">
            <p className="text-[7px] text-white/10 font-black uppercase tracking-[0.5em] mb-1.5">Settlement</p>
            <p className="font-black text-sm text-white tracking-tighter">Every_300s (5m)</p>
          </div>
          <div className="p-4">
            <p className="text-[7px] text-white/10 font-black uppercase tracking-[0.5em] mb-1.5">Basis_Guard</p>
            <p className="font-black text-sm text-white tracking-tighter">Automated_Release</p>
          </div>
        </div>
      </div>

      <button
        onClick={handleInitialClick}
        className="w-full bg-white text-black py-5 rounded-[20px] font-black text-lg uppercase tracking-tighter hover:bg-[#AEB877] transition-all shadow-2xl active:scale-95"
      >
        Start Robot
      </button>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-3xl animate-in fade-in duration-500">
          <div className="w-full max-w-sm glass-card p-6 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#AEB877] to-transparent z-10"></div>
            
            <div className="relative z-10">
              <div className="w-12 h-12 bg-[#AEB877]/10 rounded-[20px] flex items-center justify-center mx-auto mb-4 text-[#AEB877] border border-[#AEB877]/20 shadow-2xl">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="animate-pulse"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>

              <div className="text-center mb-4">
                <h2 className="text-lg md:text-xl font-black uppercase tracking-tighter text-white mb-1">Confirm_Execution</h2>
                <p className="text-[7px] text-white/20 font-black uppercase tracking-[0.4em] leading-relaxed">AI robot ready for 24-hour continuous trading protocol.</p>
              </div>

              <div className="bg-black/40 border border-white/5 rounded-xl p-4 space-y-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-[6px] font-black text-white/20 uppercase tracking-[0.5em]">Protocol_Account</span>
                  <span className={`text-[6px] font-black uppercase px-2 py-0.5 rounded-md ${accountType === AccountType.DEMO ? 'bg-[#AEB877]/10 text-[#AEB877]' : 'bg-[#FF4D4D]/10 text-[#FF4D4D]'}`}>
                    {accountType}_MODE
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[6px] font-black text-white/20 uppercase tracking-[0.5em]">Allocation</span>
                  <span className="text-lg font-black text-white tracking-tighter tabular-nums">${parseFloat(amount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[6px] font-black text-white/20 uppercase tracking-[0.5em]">Duration</span>
                  <span className="text-[8px] font-black text-white uppercase tracking-tighter">24_Hours (Continuous)</span>
                </div>
                <div className="pt-4 border-t border-white/5">
                   <div className="flex justify-between items-center">
                      <span className="text-[6px] font-black text-white/20 uppercase tracking-[0.5em]">Est_ROI_Matrix</span>
                      <span className="text-sm font-black text-[#AEB877] tabular-nums tracking-tighter glow-text">
                        +{systemSettings?.dailyMinProfit || 8}% — +{systemSettings?.dailyMaxProfit || 24}%
                      </span>
                   </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleConfirmStart}
                  className="w-full bg-[#AEB877] text-black py-4 rounded-[16px] font-black text-base uppercase tracking-tighter hover:bg-white transition-all shadow-2xl active:scale-[0.98]"
                >
                  Confirm & Start
                </button>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="w-full bg-transparent text-white/20 py-1 font-black text-[7px] hover:text-white transition-all uppercase tracking-[0.5em]"
                >
                  Cancel_Protocol
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RobotSetup;
